<template>
  <div style="background-color: white;" data-testid="annotation-suggestion-card">
    <checkbox style="float:right" :model-value="checkboxValue" @update:model-value="setChecked"></checkbox>
    <row>
      <col-8>
        <annotated-image v-if="annotation" :max_height="200" @canvas_selected="selectAsset" :image_url="asset.path" :zoom_enabled="false" :annotations="[annotation]" :sensitive-blur="sensitiveBlurForThisAsset"></annotated-image>
      </col-8>
      <col-4>
        <br>
        <annotation-suggestion-crop @select_asset="selectAsset" v-if="image_data" :annotation_type="annotation_type" :image_data="image_data" :sensitive-blur="sensitiveBlurForThisAsset"></annotation-suggestion-crop>
      </col-4>
    </row>
    <row data-testid="annotation-suggestion-title">
      {{suggestion_title}}
    </row>
  </div>
</template>

<script>
import AnnotatedImage from '@/components/AnnotatedImage'
import Checkbox from '@/components/Checkbox'
import Row from '@/components/layout/Row.vue'
import Col8 from '@/components/layout/Col8.vue'
import Col4 from '@/components/layout/Col4.vue'
import AnnotationSuggestionCrop from '@/components/AnnotationSuggestionCrop'
import LoadSettingsMixin from '@/mixins/LoadSettingsMixin'
import CurrentUserMixin from '@/mixins/CurrentUserMixin.vue'
import {
  getBlurUnlessMetaFromSettings,
  shouldBlurAssetMeta,
} from '@/utils/sensitiveImageBlur'

export default {
  name: "AnnotationSuggestion.vue",
  props: {
    'suggestion': {default: ''},
    'asset': {default: ''},
  },
  components: {AnnotatedImage, Row, Col8, Col4, Checkbox, AnnotationSuggestionCrop},
  mixins: [LoadSettingsMixin, CurrentUserMixin],
  data() {
    return {
      checkboxValue: false,
      image_data: null,
    }
  },
  methods: {
    async loadCrop() {
      if (this.annotation) {
        const firstAnnotation = this.annotation;
        let coords = firstAnnotation.coords;
        let bboxCoordsString = JSON.stringify(coords);
        let data = await this.$api.asset.get_crop(this.asset.path, bboxCoordsString, this.asset.id);
        this.image_data = data.crops[0].crop;
      }
    },
    findMatchedAnnotation() {
      let matchedAnnotation = null;
      for (const annotation of this.asset.annotations) {
        if (annotation.id === this.annotation_id) {
          matchedAnnotation = annotation;
          break;
        }
      }
      return matchedAnnotation;
    },
    setChecked(checked) {
      this.checkboxValue = checked;
      this.$emit('select_toggled', this.suggestion_id, checked)
    },
    selectAsset() {
      this.$emit('select_asset', this.asset.id)
    },
    parseSuggestion() {
      if (!this.suggestion || !this.suggestion.suggestion) {
        return {};
      }
      const raw = this.suggestion.suggestion;
      if (raw && typeof raw === 'object') {
        return raw;
      }
      try {
        return JSON.parse(raw) || {};
      } catch (e) {
        return {};
      }
    },
    resolveSuggestedLabelName(parsed) {
      if (parsed.label_name != null && String(parsed.label_name).trim() !== '') {
        return String(parsed.label_name);
      }
      if (parsed.label != null && String(parsed.label).trim() !== '') {
        return String(parsed.label);
      }
      const labels = (this.$store && this.$store.getters['label/get_items']) || [];
      const found = labels.find((l) => l.id == parsed.label_id);
      if (found) {
        return found.name;
      }
      return parsed.label_id != null ? String(parsed.label_id) : '';
    },
  },
  computed: {
    sensitiveBlurForThisAsset() {
      const policy = getBlurUnlessMetaFromSettings(this.settings || []);
      return shouldBlurAssetMeta(this.asset && this.asset.meta, policy, this.is_sensitive_image_blur_exempt);
    },
    annotation_type() {
      if (this.annotation) {
        const firstAnnotation = this.annotation;
        if (firstAnnotation.type === "box") {
          return 1;
        } else if (firstAnnotation.type === "point") {
          return 2;
        }
      }
      return 1;
    },
    annotation() {
      const parsedSuggestion = this.parseSuggestion();
      const { action } = parsedSuggestion;
      const labelName = this.resolveSuggestedLabelName(parsedSuggestion);

      if (action === "add_bounding_box") {
        const { coords } = parsedSuggestion;
        return {
          conf: 1,
          coords: coords,
          id: this.annotation_id,
          label: labelName,
          type: "box",
          color_values: [53, 209, 15],
        };
      } else if (action === "add_keypoint") {
        const { coords } = parsedSuggestion;
        return {
          conf: 1,
          coords: coords,
          id: this.annotation_id,
          label: labelName,
          type: "point",
          color_values: [53, 209, 15],
        };
      } else if (action === "delete_annotation") {
        let matchedAnnotation = this.findMatchedAnnotation();
        if (matchedAnnotation) {
          let coords = matchedAnnotation.coords;
          let label = matchedAnnotation.label;
          return {
            conf: 1,
            coords: coords,
            id: this.annotation_id,
            label: label,
            type: "box",
            color_values: [255, 0, 0], 
          };
        }
      } else if (action === "rename_label_annotation") {
        let matchedAnnotation = this.findMatchedAnnotation();
        if (matchedAnnotation) {
          let coords = matchedAnnotation.coords;
          let label = matchedAnnotation.label;
          return {
            conf: 1,
            coords: coords,
            id: this.annotation_id,
            label: label,
            type: "box",
            color_values: [25, 118, 210],
          };
        }
      }
      return null;
    },
    annotation_id() {
      let initial_suggestion = this.suggestion;
      let annotation_id = initial_suggestion.annotation_id;
      return annotation_id;
    },
    suggestion_id() {
      let initial_suggestion = this.suggestion;
      let suggestion_id = initial_suggestion.id;
      return suggestion_id;
    },
    suggestion_title() {
      const parsed_suggestion = this.parseSuggestion();
      if (!parsed_suggestion || !parsed_suggestion.action) {
        return '';
      }
      if (parsed_suggestion.action === "rename_label_annotation") {
        let suggested_label = this.resolveSuggestedLabelName(parsed_suggestion);
        let matchedAnnotation = this.findMatchedAnnotation();
        if (matchedAnnotation) {
          let original_label = matchedAnnotation.label;
          return this.$t('suggestions.rename_label_annotation', {
            original_label,
            suggested_label
          });
        }
      } else if (parsed_suggestion.action === "delete_annotation") {
        let matchedAnnotation = this.findMatchedAnnotation();
        if (matchedAnnotation) {
          let original_label = matchedAnnotation.label;
          return this.$t('suggestions.delete_annotation', {
            original_label
          });
        }
      } else if (parsed_suggestion.action === "add_bounding_box") {
        let suggested_label = this.resolveSuggestedLabelName(parsed_suggestion);
        return this.$t('suggestions.add_bounding_box', {
          suggested_label
        });
      } else if (parsed_suggestion.action === "add_keypoint") {
        let suggested_label = this.resolveSuggestedLabelName(parsed_suggestion);
        return this.$t('suggestions.add_keypoint', {
          suggested_label
        });
      }
      return parsed_suggestion;
    }
  },
  mounted() {
    this.loadCrop();
  }
}
</script>

<style scoped>
</style>
